import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { ResultDisplayWriting } from '@/components/results/ResultDisplayWriting';
import { NO_BREAK_SPACE } from '@/constant';
import { isStroke, isStrokeHistory, Result, SlideComponentHkcsQ3, WritingStrokes } from '@/types/slide';
import { Box, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ImageShowcase } from '../../ImageShowcase';
import { PropsWithOnLoad } from '../../type';
import { ResultContentContainer } from './ResultContentContainer';

type HkcsQ3Answer = {
    hkcsQ3: {
        textContent: string;
        pickedCharacterOption: string;
        answer: Array<
            {
                version?: number;
            } & WritingStrokes
        >;
    };
};

const isHkcsQ3Answer = (answer: unknown): answer is HkcsQ3Answer => {
    const ret =
        typeof answer === 'object' &&
        answer !== null &&
        'hkcsQ3' in answer &&
        typeof (answer as HkcsQ3Answer).hkcsQ3 === 'object' &&
        (answer as HkcsQ3Answer).hkcsQ3 !== null &&
        'textContent' in (answer as HkcsQ3Answer).hkcsQ3 &&
        typeof (answer as HkcsQ3Answer).hkcsQ3.textContent === 'string' &&
        'pickedCharacterOption' in (answer as HkcsQ3Answer).hkcsQ3 &&
        typeof (answer as HkcsQ3Answer).hkcsQ3.pickedCharacterOption === 'string' &&
        Array.isArray((answer as HkcsQ3Answer).hkcsQ3.answer) &&
        (answer as HkcsQ3Answer).hkcsQ3.answer.every(
            (item) =>
                item !== undefined &&
                item.normalMode !== undefined &&
                typeof item.normalMode === 'boolean' &&
                item.strokes !== undefined &&
                item.strokes !== null &&
                Array.isArray(item.strokes) &&
                (item.normalMode ? item.strokes.every(isStrokeHistory) : item.strokes.every(isStroke)),
        );
    return ret;
};

export const ResultContentHkcsQ3: React.FC<
    PropsWithOnLoad & {
        question: SlideComponentHkcsQ3 & { answers: Result[] };
        viewOnly: boolean;
    }
> = ({ question, viewOnly, onLoad }) => {
    const answer = question.answers[0];
    const data = answer?.data ? JSON.parse(answer.data) : undefined;

    const [loadCount, setLoadCount] = useState<number>((answer?.fetchResultFiles ? 1 : 0) + (question.content.imageURL || question.content.image ? 1 : 0));

    const [loading, setLoading] = useState<boolean | undefined>(answer?.fetchResultFiles ? true : false);
    const [drawing, setDrawing] = useState<string | undefined>(undefined);

    const { answer: studentAnswer, pickedCharacterOption } = isHkcsQ3Answer(data) ? data.hkcsQ3 : { answer: [], pickedCharacterOption: NO_BREAK_SPACE };

    const writingStrokes: WritingStrokes[] = studentAnswer.map((item) => {
        const rest = { ...item };
        // we aren't allowed to ignore no-unused-vars
        // nor eslint couldn't recognize underscore convention that indicates _version is intentionally to be unused
        // so just forget about whatever performance hit it has and use delete instead of a proper destructuring that is meant to do the same thing better
        delete rest.version;
        return rest as WritingStrokes;
    });

    const boundMorpheme = question.content.characterList[question.content.character]?.character;

    const handleLoadImage = () => {
        setLoadCount((prev) => prev - 1);
    };

    useEffect(() => {
        if (answer?.fetchResultFiles) {
            setLoading(true);
            answer
                .fetchResultFiles()
                .then((files) => {
                    if (files && files.length > 0) {
                        setDrawing(files[0]?.url);
                    } else {
                        setDrawing(undefined);
                        setLoadCount((prev) => prev - 1);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setDrawing(undefined);
        }
    }, [answer]);

    useEffect(() => {
        if (loadCount <= 0) {
            onLoad?.();
        }
    }, [loadCount, onLoad]);

    return (
        <ResultContentContainer>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: 594,
                        height: 240,
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                width: 185,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: 'white',
                                padding: 1,
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant='h1' textAlign={'center'} sx={{ ml: 2, color: '#555' }}>
                                {boundMorpheme}
                            </Typography>
                            <ImageShowcase
                                width={75}
                                src={
                                    question.content.imageURL && question.content.imageURL !== ''
                                        ? question.content.imageURL
                                        : `/asset/characters/${question.content.image ? question.content.image + '/image.png' : 'default.png'}`
                                }
                                onLoad={handleLoadImage}
                            />
                        </Box>
                        <PhraseHighlight sx={{ color: '#555', my: 2 }} phrase={question.content.phrase} highlight={question.content.phrase} underline={false} />
                        <Box sx={{ backgroundColor: 'white', padding: 1, borderRadius: 2 }}>
                            <Typography variant='h4' textAlign={'center'} sx={{ color: '#555' }}>
                                {pickedCharacterOption}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ height: 240, width: '100%' }}>
                        {drawing ? (
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    borderWidth: 2,
                                    display: 'flex',
                                    flex: 1,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box
                                    component={'img'}
                                    src={drawing}
                                    sx={{
                                        height: '100%',
                                        width: '100%',
                                        borderRadius: 2,
                                        borderColor: 'divider',
                                        borderWidth: 2,
                                    }}
                                    onLoad={handleLoadImage}
                                />
                            </Box>
                        ) : loading ? (
                            <Skeleton variant='rounded' sx={{ height: '100%', width: '100%', borderRadius: 2 }} />
                        ) : (
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    borderWidth: 2,
                                    flex: 1,
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '1px',
                        backgroundColor: 'divider',
                        height: 'fit-content',
                        width: 'fit-content',
                        border: 1,
                        borderColor: 'divider',
                    }}
                >
                    {[...Array(14)].map((_, idx) => {
                        return (
                            <Box
                                key={idx}
                                sx={{
                                    height: '84px',
                                    width: '84px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'white',
                                }}
                            >
                                {writingStrokes[idx] && <ResultDisplayWriting type='writingStrokes' writingStrokes={writingStrokes[idx]!} viewOnly={viewOnly} />}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </ResultContentContainer>
    );
};
